import { Select } from '@higo/ui';
import styled, { css } from 'styled-components';
import { useAppLocale } from '@higo/common/lib/features/Locale';
import { useDictionaryCollector } from '@summer/jst-react-dictionaries';
import { FC, useEffect, useMemo } from 'react';
import { collectLanguageDictionaryItems } from '@higo/common/lib/features/Dictionary';
import { useDoctorPortalApiContext } from '@higo/doctor/src/contexts/ApiContext';
import { setUserLanguage } from '@higo/api';

const StyledSelect = styled(Select)<{ $fullWidth: boolean }>(({ $fullWidth }) =>
  $fullWidth
    ? css`
        width: 100%;
      `
    : css`
        width: 8rem;
      `,
);

interface LanguageSwitchProps {
  fullWidth?: boolean;
  setLanguageToBackend?: boolean;
}

// helpers
const normalize = (code?: string) => (code ?? '').toLowerCase();
const base = (code?: string) => normalize(code).split('-')[0];
const sameLang = (a?: string, b?: string) =>
  normalize(a) === normalize(b) || base(a) === base(b);

export const LanguageSwitch: FC<LanguageSwitchProps> = ({
  fullWidth = false,
  setLanguageToBackend = false,
}) => {
  const { current, changeLocale } = useAppLocale();
  const languages = useDictionaryCollector(collectLanguageDictionaryItems);
  const { authorizedAxios } = useDoctorPortalApiContext();

  const dictNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const { code, name } of languages) {
      if (name) map.set(code, name);
      const b = base(code);
      if (b && name && !map.has(b)) map.set(b, name);
    }
    return map;
  }, [languages]);

  const displayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([current.code], { type: 'language' });
    } catch {
      return undefined;
    }
  }, [current.code]);

  const languageToShow = useMemo(() => {
    // zdefiniowane wewnątrz — brak ostrzeżenia od react-hooks/exhaustive-deps
    const unsupported = new Set(['ar-AR', 'hu', 'hu-HU', 'hu-hu', 'ro', 'ro-RO', 'ro-ro']);
    return languages.filter((item) => !unsupported.has(item.code));
  }, [languages]);

  const options = useMemo(() => {
    return languageToShow.map(({ name, code }) => {
      const b = base(code);
      const labelFromDict =
        name ||
        dictNameByCode.get(code) ||
        (b ? dictNameByCode.get(b) : undefined);

      const labelFromIntl =
        displayNames?.of(b || '') || displayNames?.of(code) || undefined;

      const label = labelFromDict || labelFromIntl || code;

      return {
        label,
        value: code,
      };
    });
  }, [languageToShow, dictNameByCode, displayNames]);

  const selectedValue = useMemo(() => {
    const match = options.find((o) => sameLang(o.value, current.code));
    return match?.value ?? current.code;
  }, [options, current.code]);

  useEffect(() => {
    if (setLanguageToBackend) {
      void setUserLanguage(authorizedAxios)(current.code);
    }
  }, [current.code, authorizedAxios, setLanguageToBackend]);

  return (
    <StyledSelect
      value={selectedValue}
      options={options}
      onChange={(code) => {
        if (!code) return;
        changeLocale(code as string);
      }}
      $fullWidth={fullWidth}
      id="language-dropdown"
    />
  );
};
